<script>
  import BaseHeading from '../BaseHeading/BaseHeading.svelte';
  import BaseSpinner from '../BaseSpinner/BaseSpinner.svelte';
  import { SPRITESHEET_URL } from '../../utils/constants';
  import { enhance } from '$app/forms';

  let {
    headingText,
    headingLevel = 'h2',
    headingClasses = 'leading-tight font-medium text-blue-dark-navy text-lg md:text-2xl xl:text-3xl',
    content = undefined,
    emailLabel,
    descriptionLabel,
    reasonLabel,
    reasonOptions,
    emailValidationErrorText,
    successText,
    errorText,
    tryAgainButtonLabel,
    submitButtonText,
    disclaimer,
    lang,
    formType = 'contactUs',
    dataTestId = 'contact-form',
  } = $props();
  
  const langParam = lang === 'es' ? '/es' : '';
  let selected = $state();

  let incomingState = 'initial';
  const initialStates = {
    initial: false,
    populated: false,
    invalid: false,
    success: false,
    failure: false,
    pending: false,
  };

  const setState = (activeState) => {
    currentState = { ...initialStates, [activeState]: true };
  };

  let currentState = $state();
  setState(incomingState);

  let activeState = $derived(currentState);

  const checkValidity = (e) => {
    if (!e.target.validity.valid) {
      setState('invalid');
    } else {
      setState('populated');
    }
  };

</script>

<div data-testid={dataTestId} class="max-w-200 mx-auto w-full" id={dataTestId}>
  <BaseHeading className={headingClasses} {headingLevel} dataTestId="{dataTestId}-base-heading">
    {headingText}
  </BaseHeading>
  {#if content}
    <p class="mt-4 md:text-lg">{@html content}</p>
  {/if}
  {#if activeState.success}
    <div class="flex flex-col min-w-lg flex-1 items-center justify-center">
      <svg class="w-16 h-16 my-4 fill-current text-blue-cerulean">
        <use xlink:href={`${SPRITESHEET_URL}#check`} />
      </svg>
      <p class="font-medium text-center md:text-lg mb-4 px-8 text-blue-navy">
        {@html successText}
      </p>
    </div>
  {:else}
    <form
      dataTestId="{dataTestId}-form"
      class="mb-4 mt-6 flex flex-col align-start justify-start"
      method="POST"
      action={`${langParam}/contact-us`}
      use:enhance={() => {        
        setState('pending');
        return async ({ result }) => {
          setState(result.type);
        };
      }}
    >
      {#if activeState.failure}
        <div class="flex flex-col min-w-lg flex-1 items-center justify-center">
          <svg class="w-12 h-12 mb-4 fill-current text-red">
            <use xlink:href={`${SPRITESHEET_URL}#alert`} />
          </svg>
          <p class="font-medium text-center md:text-lg mb-4 px-8 text-blue-navy">
            {@html errorText}
          </p>
          <div class="flex justify-center items-center mb-8">
            <button
              data-testid="{dataTestId}-try-again-button"
              class="leading-loose text-center text-white font-medium bg-blue-cerulean hover-hover:hover:bg-blue-darkish active:bg-blue-navy
              focus:outline-none focus:shadow-focus active:shadow-active rounded-md whitespace-nowrap min-w-25 px-4 flex items-center
              justify-center h-12 mt-0.5"
              formaction={`${langParam}/contact-us`}
            >
              {tryAgainButtonLabel}
            </button>
          </div>
        </div>
      {:else}
        <label class="text-sm font-medium mb-1" for="email">{emailLabel}</label>
        <input
          id="email"
          name="email"
          type="email"
          data-testid="{dataTestId}-email-input"
          class="w-full text-gray-gunmetal rounded border border-gray-gunmetal h-12 px-4 py-2 bg-white text-base
            leading-loose placeholder-gray-bluey focus:outline-none focus:shadow-focus active:shadow-active"
          class:border-red={activeState.invalid}
          class:text-red={activeState.invalid}
          class:border-gray-gunmetal={activeState.initial}
          class:text-gray-gunmetal={activeState.initial}
          readonly={activeState === 'pending'}
          required
          aria-required="true"
          onblur={checkValidity}
        />
        <span
          data-testid="{dataTestId}-validation-message"
          class="text-sm text-red leading-loose"
          class:invisible={!activeState.invalid}
          class:visible={activeState.invalid}
        >
          {emailValidationErrorText}
        </span>
        {#if formType === 'contactUs' && reasonLabel && reasonOptions}
          <label class="text-sm font-medium mb-1" for="reason">{reasonLabel}</label>
          <select
            bind:value={selected}
            disabled={activeState === 'pending'}
            data-testid="{dataTestId}-reasons"
            id="reason"
            name="reason"
            class="w-full text-gray-gunmetal rounded border border-gray-gunmetal px-3 py-2 h-12 bg-white text-base
                leading-loose placeholder-gray-bluey focus:outline-none focus:shadow-focus mb-8 active:shadow-active"
          >
            {#each reasonOptions as option}
              <option value={option.optionText}>
                {option.optionText}
              </option>
            {/each}
          </select>
        {/if}
        <label class="text-sm font-medium mb-1" for="description">{descriptionLabel}</label>
        <textarea
          data-testid="{dataTestId}-description-textarea"
          readonly={activeState === 'pending'}
          id="description"
          name="description"
          class="w-full text-gray-gunmetal rounded border border-gray-gunmetal px-4 py-2 bg-white text-base
              leading-loose placeholder-gray-bluey focus:outline-none focus:shadow-focus active:shadow-active mb-4"
          rows="5"
          maxlength="1024"
          required
          aria-required="true"
        ></textarea>

        {#if disclaimer}
          <div class="markdown-wrapper my-4 text-sm leading-tight text-gray-warm">
            {@html disclaimer.content}
          </div>
        {/if}
        <button
          data-testid="{dataTestId}-button"
          type="submit"
          disabled={activeState === 'pending'}
          class="flex-shrink-0 leading-loose text-center font-medium text-white bg-blue-cerulean hover-hover:hover:bg-blue-darkish
              active:bg-blue-navy focus:outline-none focus:shadow-focus active:shadow-active rounded-md whitespace-nowrap min-w-25
              px-8 flex items-center justify-center h-16 md:h-12 mx-auto md:mx-0 cursor-pointer w-40"
        >
          {#if activeState === 'pending'}
            <BaseSpinner dataTestId="{dataTestId}-spinner" />
          {:else}
            {submitButtonText}
          {/if}
        </button>
      {/if}
    </form>
  {/if}
</div>
